import { Divider, Skeleton } from '@mui/material';
import { moneyFormat } from 'common/numberHelpers';
import { checkSubscriptionResource } from 'common/subscription/checkSubscriptionResource';
import {
  DataBlockCourse,
  IDataBlockCourse,
} from 'design-system/components/DataBlock/Course';
import { IconButton } from 'design-system/components/IconButton';
import { Typography } from 'design-system/components/Typography';
import { useContext } from 'react';
import { UseQueryResult } from 'react-query';
import { CourseDetailPermissionContext } from 'state/course/CourseDetailPermissionProvider';
import { ICourseDetailResponse } from '../../../interfaces/training.interface';
import CourseDetailInfo from './CourseDetailInfo';

type CourseSidebarProps = {
  toggleModalRequestCertificate: () => void;
};

const CourseSidebar = ({
  toggleModalRequestCertificate,
}: CourseSidebarProps) => {
  const { courseDetailQueryReturn, latestSubscription } = useContext(
    CourseDetailPermissionContext
  );

  const { data: course, isLoading } = courseDetailQueryReturn as UseQueryResult<
    ICourseDetailResponse,
    unknown
  >;

  const { coursePrice, certificateShortName, userOrder } = course || {};
  const { isCertificateRequested, isCompleted } = course?.userProgress || {};

  const isPaidCourse = coursePrice?.isPaidCourse;
  const userHasPaid =
    userOrder?.hasUserPaid ||
    checkSubscriptionResource(latestSubscription).course.accessToCourse;

  const condition = isCompleted || userHasPaid;

  const renderDataBlock = () => {
    if (!condition) return null;

    const isNotCertificateRequested =
      !isCertificateRequested && certificateShortName;

    const labelContent =
      isCertificateRequested && isCompleted
        ? 'Đã gửi yêu cầu nhận chứng chỉ'
        : isNotCertificateRequested && isCompleted
        ? 'Yêu cầu cấp chứng chỉ ngay!'
        : userHasPaid
        ? 'Học khoá học ngay thôi!'
        : '';

    const valueContent = userHasPaid ? 'Đã thanh toán' : 'Đã hoàn thành';

    const props: IDataBlockCourse = {
      value: valueContent,
      label: labelContent,
      iconName: 'done',
      className: 'bg-Success/Main text-white cursor-pointer',
      onClick:
        !isCertificateRequested && certificateShortName && isCompleted
          ? toggleModalRequestCertificate
          : undefined,
      ...(!userHasPaid && {
        endAdornment: (
          <IconButton
            className="text-white"
            icon={{
              name: 'send',
            }}
          />
        ),
      }),
    };

    return <DataBlockCourse {...props} />;
  };

  if (isLoading) return <SkeletonCourseSidebar />;

  return (
    <div className="sticky left-0 top-[70px] mt-4 h-fit space-y-4">
      {renderDataBlock()}
      <div
        className={`flex h-fit w-[350px] flex-col gap-6 rounded-lg border border-Primary/Main bg-white p-6 ${
          condition && 'border-Success/Main'
        }`}
      >
        {!condition && (
          <>
            <Typography variant="heading1" className="text-Success/Main">
              {isPaidCourse
                ? `${moneyFormat(coursePrice.price || 0)} VNĐ`
                : 'Miễn phí'}
            </Typography>
            <Divider />
          </>
        )}
        <CourseDetailInfo />
      </div>
    </div>
  );
};

export default CourseSidebar;

const SkeletonCourseSidebar = () => (
  <div className="h-fit w-[350px] bg-white p-6">
    <Skeleton variant="text" className="mb-4" />
    <Skeleton variant="text" className="mb-4" />
    <Skeleton variant="text" className="mb-4" />
  </div>
);
